import { SearchOutlined, UnorderedListOutlined, LeftOutlined, MergeCellsOutlined, PartitionOutlined, LinkOutlined, ExpandOutlined, InfoOutlined, FileMarkdownOutlined, HistoryOutlined } from '@ant-design/icons'
import { Collapse, Flex, Spin, Input, Typography, Table, Layout, Space, Button, Tooltip, Drawer, Radio, Empty, App } from 'antd'
import { useEffect, useMemo, useState, useRef } from 'react'
import { useCustomerProfileStore } from '@/stores/customer-profile'
import { isNotEmpty, isEmpty } from '@haina/utils-commons'
import { Conditional } from '@/components/Conditional'
import { SimpleMarkdown } from './components/simple-markdown'
import { LineItem } from './components/line-item'
import { CustomerOrderList } from './components/customer-order-list'
import { ProfileFooter } from './components/profile-footer'

import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function AllInOne() {

  const customerListColumns = [
    {
      title: '名字/邮箱',
      dataIndex: 'customer_name',
      render: (_, customer) => {
        return (
          <Space direction="vertical">
            <Conditional
              condition={customer.global_customer_id === selectedCustomerId}
              whenTrue={
                <Radio defaultChecked checked={true}>{customer.customer_name}</Radio>
              }
              whenFalse={<Typography.Text>{customer.customer_name}</Typography.Text>}
            />
            <Typography.Text type="secondary">{customer.customer_email}</Typography.Text>
          </Space>
        )
      }
    },
    {
      title: '订单号',
      dataIndex: 'order_id',
      width: '150px',
    },
  ]

  const [spinning, setSpinning] = useState(false)
  const [masterDrawerOpen, setMasterDrawerOpen] = useState(false)
  const [selectedCustomerId, setSelectedCustomerId] = useState(0)
  const keywordRef = useRef(null)

  const { notification, message } = App.useApp()

  const [collapseProfileList, setCollapseProfileList] = useState([])

  const [
    customerList, fetchCustomerList,
    customerDetail, fetchCustomerDetail,
    contactList, fetchContactList,
    orderList, fetchOrderList,
    orderProfile, fetchOrderProfile,
    customerSummary, fetchCustomerSummary,
    customerMaster, fetchCustomerMaster
  ] = useCustomerProfileStore((s) => [
    s.customerList, s.fetchCustomerList,
    s.customerDetail, s.fetchCustomerDetail,
    s.contactList, s.fetchContactList,
    s.orderList, s.fetchOrderList,
    s.orderProfile, s.fetchOrderProfile,
    s.customerSummary, s.fetchCustomerSummary,
    s.customerMaster, s.fetchCustomerMaster,
  ])

  const showMasterDrawer = () => {
    setMasterDrawerOpen(true);
  };
  const onMasterDrawerClose = () => {
    setMasterDrawerOpen(false);
  };

  const calculateAge = (birthday) => {
    if (!birthday) return '-';

    const birthDate = new Date(birthday);

    if (isNaN(birthDate.getTime())) {
      return '-';
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  const customerAge = useMemo(() => {
    return calculateAge(customerDetail.birthday);
  }, [customerDetail.birthday]);

  const searchCustomerList = () => {
    const keyword = keywordRef.current.input.value
    if (isEmpty(keyword)) {
      notification.warning({ message: '请问你想搜什么？', description: '至少给我点提示吧🤣', placement: 'topLeft' });
      return
    }
    setSpinning(true)
    fetchCustomerList(keywordRef.current.input.value).then((result) => {
      console.info('searchCustomerList: ', result)
    }).catch(ex => {
      notification.warning({ message: '出错了', description: ex?.message, placement: 'topLeft' });
    }).finally(() => {
      setSpinning(false)
    })
  }

  const getProfileConfig = useMemo(() => ({
    L1: { key: '1', label: 'L1 咨询阶段', extra: '顾问' },
    L2: { key: '2', label: 'L2 走团阶段', extra: '导游' },
    L3: { key: '3', label: 'L3 复盘激活', extra: '客运' }
  }), []);

  const loadOrderProfile = (orderSn) => {
    fetchOrderProfile(orderSn).then((result) => {
      console.info('fetchOrderProfile: ', result)

      const getKey = (profile) => {
        if (profile.milestone === 'L1') {
          return '1'
        }
        if (profile.milestone === 'L2') {
          return '2'
        }
        if (profile.milestone === 'L3') {
          return '3'
        }
      }
      const getLabel = (profile) => {
        if (profile.milestone === 'L1') {
          return 'L1 咨询阶段'
        }
        if (profile.milestone === 'L2') {
          return 'L2 走团阶段'
        }
        if (profile.milestone === 'L3') {
          return 'L3 复盘激活'
        }
      }
      const getExtra = (profile) => {
        if (profile.milestone === 'L1') {
          return '顾问'
        }
        if (profile.milestone === 'L2') {
          return '导游'
        }
        if (profile.milestone === 'L3') {
          return '客运'
        }
      }
      const profileMapList = result.map((profile) => {
        const config = getProfileConfig[profile.milestone]
        if (!config) {
          return {
            key: profile.milestone || 'unknown',
            label: `${profile.milestone || 'Unknown'} 阶段`,
            children: <>{SimpleMarkdown(profile.insight)}</>,
            extra: <span>未知</span>,
          };
        }
        return {
          key: config.key,
          label: config.label,
          children: <>
            {SimpleMarkdown(profile.insight)}
            <ProfileFooter prompt={profile?.prompt_content} input={profile?.user_query} model={profile.model} datetime={profile.updated_time} />
          </>,
          extra: (
            <>
              <span>👩‍🦱 {config.extra}</span>
            </>
          ),
        }
      })
      setCollapseProfileList(profileMapList)
    })
  }

  const loadCustomerDetail = async (customer) => {
    setSpinning(true)
    console.info('customer: ', customer)
    const customerId = customer.global_customer_id
    setSelectedCustomerId(customer.global_customer_id)

    try {
      const [detailResult, summaryResult, masterResult, contactResult, orderResult] = await Promise.all([
        fetchCustomerDetail(customerId),
        fetchCustomerSummary(customerId),
        fetchCustomerMaster(customerId),
        fetchContactList(customerId),
        fetchOrderList(customerId)
      ]);

      console.info('masterResult: ', masterResult)

      if (orderResult.length > 0) {
        await loadOrderProfile(orderResult[0].order_sn);
      } else {
        setCollapseProfileList([])
      }
      // throw new Error('获取数据失败');
    } catch (error) {
      notification.error({ message: '失败是成功之母🤣', description: error.message, placement: 'top' });
    } finally {
      setSpinning(false);
    }
  }

  const formatContactName = (type) => {
    switch (type) {
      case 'email':
        return '邮箱';
      case 'phone_number':
        return '手机';
      case 'whatsapp':
        return 'WhatsApp';
      default:
        return '未知';
    }
  }

  const formatBirthday = (dateString) => {
    if (!dateString) return '-';

    const date = dayjs(dateString);

    if (!date.isValid()) {
      return '-';
    }

    return date.format('YYYY-MM-DD')
  }

  return (
    <>
    <Drawer
      title='✨ 主档案'
      size='large'
      closable={{ 'aria-label': 'Close Button' }}
      onClose={onMasterDrawerClose}
      open={masterDrawerOpen}
    >
      {SimpleMarkdown(customerMaster?.insight)}
      <ProfileFooter prompt={customerMaster?.prompt_content} input={customerMaster?.user_query} model={customerMaster.model} datetime={customerMaster.updated_time} />
    </Drawer>
    <Spin spinning={spinning} fullscreen />
      <Layout>
        <Layout.Sider width='380' theme='light' className='relative'>
          <Typography.Title level={4}>客人列表</Typography.Title>
          <Space.Compact style={{ width: '100%' }}>
            <Input allowClear placeholder='名字、邮箱、订单号' ref={keywordRef} onPressEnter={() => {
                  searchCustomerList()
                }} />
            <Tooltip title='搜索'>
              <Button
                type='primary'
                icon={<SearchOutlined />}
                onClick={() => {
                  searchCustomerList()
                }}
              />
            </Tooltip>
          </Space.Compact>

          <Table
            rowKey='global_customer_id'
            pagination={{
              pageSize: 10,
              showQuickJumper: false,
              showLessItems: false,
              showSizeChanger: false,
              simple: { readOnly: true },
              size: "small",
              position: ['topLeft'],
            }}
            columns={customerListColumns}
            dataSource={customerList}
            onRow={(customer) => {
              return {
                onClick: () => {
                  loadCustomerDetail(customer)
                },
              }
            }}
          />
        </Layout.Sider>
        <Layout.Content style={{ minWidth: '360px' }} className='bg-white px-5'>
          <Typography.Title level={4}>✨ 客人小结</Typography.Title>

            {isEmpty(customerSummary?.insight) ? <Flex><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /></Flex> : <div className='space-y-2 pb-4 border-solid border-0 border-b border-gray-200'>
              {SimpleMarkdown(customerSummary?.insight)}

            <ProfileFooter prompt={customerSummary?.prompt_content} input={customerSummary?.user_query} model={customerSummary.model} datetime={customerSummary.updated_time} />
            </div>}

          <Typography.Title level={5}>所有订单</Typography.Title>
          <CustomerOrderList dataSource={orderList} onClick={(order) => {
            console.info('click order: ', order)
            loadOrderProfile(order.order_sn)
          }} />

          <Typography.Title level={5}>✨ 客户旅程</Typography.Title>

          <Conditional
            condition={collapseProfileList.length > 0}
            whenTrue={
              <Collapse
                items={collapseProfileList}
                defaultActiveKey={['1', '2', '3']}
                bordered={false}
              />
            }
            whenFalse={<Flex><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /></Flex>}
          />

        </Layout.Content>
        <Layout.Sider
          width='400'
          theme='light'
          >
          <Typography.Title level={4}>客人档案</Typography.Title>

          <div className='bg-white p-1 max-w-4xl mx-auto pb-4 border-solid border-0 border-b border-gray-200'>

            <LineItem title='名字' value={(customerDetail?.first_name??'-') + ' ' + (customerDetail?.last_name??'-')} />
            <LineItem title='性别' value={customerDetail?.gender} />
            <LineItem title='年龄' value={customerAge} />
            <LineItem title='生日' value={formatBirthday(customerDetail?.birthday)} />
            <LineItem title='国籍' value={customerDetail.country} />
            <Flex justify='flex-end'>
              <Button type='text' icon={<ExpandOutlined />} onClick={() => {
                showMasterDrawer()
              }}>更多</Button>
            </Flex>
          </div>
          <br />

          <Typography.Title level={5}>联系方式</Typography.Title>

          <div className='bg-white p-1 max-w-4xl mx-auto pb-4 border-solid border-0 border-b border-gray-200'>
            {contactList.map((item, index) => {
                return (
                  <LineItem key={index} title={formatContactName(item.contact_type)}
                  value={
                    <Button type='link' size='small'>{item.contact_value}</Button>
                  }/>
                )
              })}
            <Flex justify='flex-end'>
              <Button type='text' icon={<MergeCellsOutlined />} onClick={() => {
                message.info('合不了')
              }}>合并</Button>
              <Button type='text' icon={<PartitionOutlined />} onClick={() => {
                message.info('没法拆')
              }}>拆分</Button>
            </Flex>
          </div>

          <br />

          <Typography.Title level={5}>社媒</Typography.Title>
          <div className='bg-white p-1 max-w-4xl mx-auto'>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          {/* <LineItem variant='link' value='https://www.facebook.com/jennifergonzalon' />
          <LineItem variant='link' value='https://www.linkedin.cn/jennifergonzalon' />
          <LineItem variant='link' value='https://www.youtube.com/@jennifergonzalon' />
          <LineItem variant='link' value='https://www.twitter.com/jennifergonzalon' /> */}
          </div>

        </Layout.Sider>
      </Layout>
    </>
  )
}

export default AllInOne
